import { CurrencyDollarIcon, ShieldIcon, SyringeIcon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { Swiper as SwiperType } from 'swiper';
import 'swiper/swiper.min.css';

import { usePets } from '../../../context/PetsContext';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { PetForm_2 as PetFormType_2, PetForm_2_Defaults } from '../../../types/Pet/types';
import { formatMiles } from '../../../utils/planes';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import './PetForm_2.scss';
import { PetForm_2Props } from './types';

const ICON_PROPS = { size: '30' } as const;

const CheckIcon = ({ size = 36 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="var(--ion-color-primary)" />
    <path
      d="M8 12L11 15L16 9"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BenefitItem: React.FC<{ icon: React.ReactNode; text: string }> = ({
  icon,
  text,
}) => (
  <div className="benefit-item">
    {icon}
    <span>{text}</span>
  </div>
);

const PlanInfo: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="plan-info">
    {icon}
    <div>
      <p className="plan-label">{label}</p>
      <p className="plan-value">${formatMiles(value)}</p>
    </div>
  </div>
);
const PetForm_2: React.FC<PetForm_2Props> = ({
  planes,
  plan,
  setPlan,
  preciosPlanes,
  goForward,
}) => {
  const { ref, handleEnter } = useEnter();
  const swiperRef = useRef<SwiperType | null>(null);
  const { datosExtra } = usePets();
  const planesTexto = datosExtra?.planes?.texto;

  const { control, handleSubmit, errors, setValue } = useForm<PetFormType_2>({
    defaultValues: PetForm_2_Defaults,
  });

  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);

  const handleSelect = useCallback(
    (planId: number , index: number) => {
      setSelectedPlan(planId);
      setValue('plan', planId);
      swiperRef.current?.slideTo(index);
    },
    [setValue],
  );

  const isValidPlan = useCallback(
    (planId?: number) => !!planId && !!planes?.some((p) => p.id === planId),
    [planes],
  );

  const onSubmit = ({ plan: selectedValue }: PetFormType_2) => {
    if (!isValidPlan(selectedValue)) {
      return;
    }

    setPlan(selectedValue);
    goForward();
  };

  useEffect(() => {
    if (plan) {
      const index = planes?.findIndex((p) => p.id === plan) ?? -1;
      if (index >= 0) handleSelect(plan, index);
    }
  }, [plan, planes, handleSelect]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyUp={handleEnter}
      className="pet-form-2 ion-padding-vertical"
    >
      <FormItem
        name="plan"
        control={control}
        errors={errors}
        rules={{
          required: {
            value: true,
            message: 'Por favor, elija un plan de cobertura',
          },
          validate: (selectedValue) =>
            isValidPlan(selectedValue) || 'Por favor, elija un plan de cobertura',
        }}
        render={() => (
          <div className="plan-swiper-container">
            <Swiper
              spaceBetween={15}
              slidesPerView={1.25}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
            >
              {planes?.map((p, i) => {
                const isSelected = selectedPlan === p.id;
                const precioPorEdad = preciosPlanes?.[i];

                return (
                  <SwiperSlide key={i}>
                    <div
                      className={`plan-card ${isSelected ? 'active' : ''}`}
                      onClick={() => handleSelect(p.id, i)}
                    >
                      {isSelected && (
                        <div className="check-icon">
                          <CheckIcon />
                        </div>
                      )}

                      <h3 className="plan-name">{p.nombre}</h3>

                      <PlanInfo
                        icon={<ShieldIcon {...ICON_PROPS} />}
                        label="Cobertura"
                        value={String(p.suma_asegurada)}
                      />

                      <PlanInfo
                        icon={<CurrencyDollarIcon {...ICON_PROPS} />}
                        label="Costo Mensual"
                        value={precioPorEdad}
                      />

                      {planesTexto && (
                        <div className="plan-benefits">
                          <BenefitItem
                            icon={<SyringeIcon size={22} />}
                            text={`${planesTexto}${planesTexto?.trim().endsWith('.') ? '' : '.'}`}
                          />
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}
      />

      <Button ref={ref} type="submit">
        Siguiente
      </Button>
    </form>
  );
};

export default React.memo(PetForm_2);
